'use client'

import { use, useEffect, useState } from 'react'

import ViverseWorldCard from '@/components/ViverseWorldCard'
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton'
import {
  RECOMMEND_VISITOR_ID_COOKIE_KEY,
  RECOMMEND_WORLDS_LIMIT,
  type RecommendVariant,
} from '@/constants/recommend'
import { useBISdkContext } from '@/context/BISdkContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { getClientCookie } from '@/lib/client-cookie'
import { cn } from '@/lib/utils'
import { getCardType } from '@/lib/viverse-world-card'
import { ContentTypeCode, RecommendRoom } from '@/services/cms'
import { fetchRecommendWorlds } from '@/services/world-recommendations'

const cardClassNames = {
  mobile: 'w-full',
  desktop: 'w-full max-w-[200px] md:max-w-full',
  skeleton: 'w-full aspect-[1.72]',
} as const

type RecommendWorldsProps = {
  roomId: string
  variant: RecommendVariant
  contentTypeCode?: ContentTypeCode
  roomsPromise: Promise<RecommendRoom[]> | null
}

export function RecommendWorldsSkeleton() {
  return Array.from({ length: RECOMMEND_WORLDS_LIMIT }).map((_, index) => (
    <ViverseWorldCardSkeleton key={index} className={cardClassNames.skeleton} />
  ))
}

function RecommendWorlds({ roomId, variant, contentTypeCode, roomsPromise }: RecommendWorldsProps) {
  const { isLoadedBISdk, isBISdkFailed } = useBISdkContext()
  const { appConfig } = useConfigsContext()
  const { isMobile } = useUAContext()
  const initialRecommendWorlds = roomsPromise ? use(roomsPromise) : null
  const [recommendWorlds, setRecommendWorlds] = useState<RecommendRoom[]>([])
  const [isLoading, setIsLoading] = useState(roomsPromise === null)
  const shouldWaitForBISdk = Boolean(appConfig?.features?.bi_sdk_uri)

  const shouldSkipClientFetch =
    Boolean(roomsPromise) || (shouldWaitForBISdk && !isLoadedBISdk && !isBISdkFailed)

  useEffect(() => {
    if (shouldSkipClientFetch) return

    let isCancelled = false

    const loadRecommendWorlds = async () => {
      setIsLoading(true)

      // NOTE: Fetch on the client ONLY when SSR could not provide the recommendation list.
      // csdataBid is read from document.cookie because iframe-embedded pages may not
      // attach cookies to the recommendation request.
      try {
        const result = await fetchRecommendWorlds({
          limit: RECOMMEND_WORLDS_LIMIT,
          variant,
          worldId: roomId,
          contentTypeCode,
          csdataBid: getClientCookie(RECOMMEND_VISITOR_ID_COOKIE_KEY),
        })

        if (isCancelled) return

        setRecommendWorlds(result.ok ? result.rooms : [])
      } catch {
        if (isCancelled) return

        setRecommendWorlds([])
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadRecommendWorlds()

    return () => {
      isCancelled = true
    }
  }, [contentTypeCode, roomId, shouldSkipClientFetch, variant])

  const worlds = initialRecommendWorlds ?? recommendWorlds

  if (isLoading) {
    return <RecommendWorldsSkeleton />
  }

  return (
    <>
      {worlds.map((world) => (
        <div key={world.id}>
          <ViverseWorldCard
            className={cn(cardClassNames.mobile, !isMobile && cardClassNames.desktop)}
            info={world}
            hideLiked
            cardOnly={isMobile}
            cardType={getCardType(world.content_type)}
          />
        </div>
      ))}
    </>
  )
}

export default RecommendWorlds
